import type {
  CalendarEventFeedbackAnswer,
  CalendarEventFeedbackQuestion
} from 'shared-types';

type Params = {
  answers: Pick<
    CalendarEventFeedbackAnswer,
    'answer' | 'CalendarEventFeedbackQuestionId'
  >[];
  questions: CalendarEventFeedbackQuestion[];
};

/**
 * The form values matching a set of submitted answers, so the feedback form
 * can be shown filled in and disabled. Used both by the participant's own
 * submission view and by the owner reading someone else's response, so the
 * two cannot disagree on how an answer is displayed. Field naming follows
 * `CalendarEventFeedbackSubmissionQuestion`.
 *
 * `question` (not `answer`) is switched on so its `data` narrows to the
 * matching shape; `answer` is then narrowed the same way as a safety net in
 * case its `type` ever failed to match the question it answers. An
 * unanswered question is left out, which shows up as an empty field.
 */
const buildCalendarEventFeedbackFormValues = ({ answers, questions }: Params) =>
  questions.reduce<Record<string, unknown>>((values, question) => {
    const answer = answers.find(
      (submitted) => submitted.CalendarEventFeedbackQuestionId === question.id
    )?.answer;
    if (!answer) return values;

    const field = `answer_${question.id}`;

    if (question.type === 'openquestion') {
      return answer.type === 'openquestion'
        ? { ...values, [field]: answer.text }
        : values;
    }

    if (question.type === 'starrating') {
      return answer.type === 'starrating'
        ? { ...values, [field]: answer.rating }
        : values;
    }

    if (question.type === 'singlechoice') {
      return answer.type === 'singlechoice'
        ? { ...values, [field]: answer.optionIds[0] }
        : values;
    }

    // multiplechoice - the checkboxes are an array of booleans positioned by
    // option, so every option needs a value, not just the selected ones
    const optionIds = answer.type === 'multiplechoice' ? answer.optionIds : [];

    return {
      ...values,
      [`${field}_options`]: question.data.options.map((option) =>
        optionIds.includes(option.id)
      )
    };
  }, {});

export default buildCalendarEventFeedbackFormValues;
